import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { PageInsightsWebSocketMessage, PageInsightsRequest } from "../types";
import { Page, Widget } from "@/modules";
import { useAuth } from "@/modules/auth";
import { canViewWidget, canViewAIInsights } from "@/modules/widgets/utils/permissions";

type PageInsightsState =
    | { status: "idle" }
    | { status: "loading"; step: string; logs: string[] }
    | { status: "done"; insights: string; recommendations: string | null; logs: string[] }
    | { status: "error"; error: string; logs: string[] };

export const usePageInsightsWebSocket = () => {
    const { state, sendMessage, isReady } = useWebSocketContext();
    const { user } = useAuth();
    const [insightsState, setInsightsState] = useState<PageInsightsState>({ status: "idle" });
    const sessionIdRef = useRef<string | null>(null);

    useEffect(() => {
        const msg = state.lastMessage as unknown as PageInsightsWebSocketMessage;
        if (!msg || msg.action !== "page-insights-v3") return;
        if (!sessionIdRef.current || msg.data?.sessionId !== sessionIdRef.current) return;

        const { data } = msg;

        if (data.type === "step") {
            setInsightsState((prev) => ({
                status: "loading",
                step: data.step,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        } else if (data.type === "log") {
            setInsightsState((prev) => {
                if (prev.status === "idle") return prev;
                const logs = [...((prev as any).logs ?? []), data.message];
                return { ...prev, logs } as PageInsightsState;
            });
        } else if (data.type === "done") {
            setInsightsState((prev) => ({
                status: "done",
                insights: data.insights,
                recommendations: data.recommendations,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        } else if (data.type === "error") {
            setInsightsState((prev) => ({
                status: "error",
                error: data.error,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        }
    }, [state.lastMessage]);

    const sendRequest = useCallback(
        (page: Omit<Page, "widgets"> & { widgets: (Widget & { data: any })[] }, dealerId: string, oemName?: string, dashboardFilters?: { dateRange: { from: string; to: string } }) => {
            const sessionId = `page-insights-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            sessionIdRef.current = sessionId;
            setInsightsState({ status: "loading", step: "Starting...", logs: [] });

            const visibleWidgets = page.widgets.filter(
                (w) => canViewWidget(w as any, user) && canViewAIInsights(w as any, user)
            );
            const widgetsWithFilters = visibleWidgets.map(w => ({
                ...w,
                ...(dashboardFilters ? { filters: { ...((w as any).filters ?? {}), dateRange: dashboardFilters.dateRange } } : {}),
            }));
            const tooLarge = JSON.stringify(widgetsWithFilters).length > 100 * 1024;
            const widgets = tooLarge ? widgetsWithFilters.map(({ data: _, ...w }) => ({ ...w, data: undefined })) : widgetsWithFilters;

            const request: PageInsightsRequest = {
                action: "page-insights-v3",
                sessionId,
                dealerId,
                page: { ...page, widgets },
                ...(oemName ? { oemName } : {}),
            };

            sendMessage(JSON.stringify(request));
        },
        [sendMessage]
    );

    const reset = useCallback(() => {
        sessionIdRef.current = null;
        setInsightsState({ status: "idle" });
    }, []);

    return {
        isReady,
        insightsState,
        sendRequest,
        reset,
    };
};
